import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../contexts/SocketContext';
import api from '../../services/api';
import { formatDuration } from '../../utils/duration';

const ActiveUsersWidget = () => {
  const navigate = useNavigate();
  const { activeUsersCount, activeUserIds } = useSocket();
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveUsers = async () => {
      try {
        const response = await api.get('/sessions/active');
        setActiveUsers(response.data.data || []);
      } catch (error) {
        console.error('Error fetching active users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveUsers();
  }, [activeUsersCount]);

  const handleClick = () => {
    navigate('/admin/user-activity');
  };

  return (
    <div 
      className="bg-white dark:bg-admin-slate-800 rounded-xl p-6 shadow-sm border border-admin-slate-200 dark:border-admin-slate-700 cursor-pointer hover:shadow-md transition-all duration-200"
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-admin-slate-600 dark:text-admin-slate-400">
            Active Users
          </p>
          <div className="mt-2 flex items-baseline gap-2">
            <p className="text-2xl font-semibold text-admin-slate-900 dark:text-admin-slate-100">
              {loading ? '...' : activeUsersCount}
            </p>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Online
              </span>
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <svg className="w-8 h-8 text-admin-slate-400 dark:text-admin-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
      </div>

      {/* Active users list */}
      {activeUsers.length > 0 && (
        <div className="mt-4 pt-4 border-t border-admin-slate-200 dark:border-admin-slate-700">
          <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 mb-2">
            Currently online:
          </p>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {activeUsers.slice(0, 3).map((session) => (
              <div key={session._id} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-admin-ucla-100 dark:bg-admin-ucla-900/30 flex items-center justify-center">
                    <span className="text-admin-ucla-600 dark:text-admin-ucla-400 font-medium text-xs">
                      {session.userId?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="text-admin-slate-700 dark:text-admin-slate-300 truncate">
                    {session.userId?.name || 'Unknown User'}
                  </span>
                </div>
                <span className="text-admin-slate-500 dark:text-admin-slate-400">
                  {formatDuration(new Date() - new Date(session.loginTime))}
                </span>
              </div>
            ))}
            {activeUsers.length > 3 && (
              <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 text-center">
                +{activeUsers.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-admin-slate-200 dark:border-admin-slate-700">
        <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400 text-center">
          Click to view details
        </p>
      </div>
    </div>
  );
};

export default ActiveUsersWidget;
